import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { 
  Loader2, 
  Plus, 
  Trash2, 
  RefreshCw, 
  Twitter,
  AlertCircle,
  CheckCircle2,
  Clock
} from 'lucide-react';
import { xAccountsApi } from '@/utils/api';
import { useUIStore } from '@/store';
import type { XAccount } from '@/types';

const statusColors: Record<string, string> = {
  active: 'bg-green-500',
  error: 'bg-red-500',
  cooldown: 'bg-yellow-500',
  pending: 'bg-blue-500',
};

const statusLabels: Record<string, string> = {
  active: '正常',
  error: '错误',
  cooldown: '冷却中',
  pending: '待登录',
};

export default function XAccountsPage() {
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { addToast } = useUIStore();

  const { data: accounts, isLoading, refetch } = useQuery({
    queryKey: ['x-accounts'],
    queryFn: () => xAccountsApi.getAccounts(),
  });

  const loginMutation = useMutation({
    mutationFn: (data: { username: string; email: string; password: string }) =>
      xAccountsApi.login(data.username, data.email, data.password),
    onSuccess: () => {
      addToast({
        title: '登录成功',
        description: 'X 账号已添加并登录',
      });
      setShowAddDialog(false);
      setUsername('');
      setEmail('');
      setPassword('');
      refetch();
    },
    onError: (error: any) => {
      addToast({
        title: '登录失败',
        description: error.response?.data?.detail || error.message,
        variant: 'destructive',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => xAccountsApi.delete(id),
    onSuccess: () => {
      addToast({
        title: '删除成功',
        description: 'X 账号已删除',
      });
      refetch();
    },
  });

  const handleLogin = () => {
    if (!username || !email || !password) {
      addToast({
        title: '信息不完整',
        description: '请填写所有字段',
        variant: 'destructive',
      });
      return;
    }
    loginMutation.mutate({ username, email, password });
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Twitter className="w-6 h-6" />
            X (Twitter) 账号管理
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            添加 X 账号以抓取推文
          </p>
        </div>
        <Button onClick={() => setShowAddDialog(true)}>
          <Plus className="w-4 h-4 mr-2" />
          添加账号
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : accounts && accounts.length > 0 ? (
        <div className="space-y-4">
          {accounts.map((account: XAccount) => (
            <Card key={account.id}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold">@{account.username}</h3>
                      <div className={`w-2 h-2 rounded-full ${statusColors[account.status] || 'bg-gray-500'}`} />
                      <Badge variant="outline" className="text-xs">
                        {statusLabels[account.status] || account.status}
                      </Badge>
                    </div>
                    
                    {account.status_message && (
                      <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {account.status_message}
                      </p>
                    )}
                    
                    <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                      {account.last_fetch_at && (
                        <span className="flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3" />
                          最后抓取: {new Date(account.last_fetch_at).toLocaleString()}
                        </span>
                      )}
                      {account.cooldown_until && (
                        <span className="flex items-center gap-1 text-yellow-600">
                          <Clock className="w-3 h-3" />
                          冷却至: {new Date(account.cooldown_until).toLocaleString()}
                        </span>
                      )}
                      {account.last_tweet_id && (
                        <span className="text-xs">
                          最后推文ID: {account.last_tweet_id.slice(0, 20)}...
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => refetch()}
                    >
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => deleteMutation.mutate(account.id)}
                      disabled={deleteMutation.isPending}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <Twitter className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground font-medium">暂无 X 账号</p>
            <p className="text-sm text-muted-foreground mt-2">
              添加 X 账号以开始抓取推文
            </p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={() => setShowAddDialog(true)}
            >
              <Plus className="w-4 h-4 mr-2" />
              添加第一个账号
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Add Account Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>添加 X (Twitter) 账号</DialogTitle>
            <DialogDescription>
              使用 twikit 库登录 X 账号。您的密码将被加密存储。
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">用户名 (username)</label>
              <Input
                placeholder="@username"
                value={username}
                onChange={(e) => setUsername(e.target.value.replace('@', ''))}
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">邮箱</label>
              <Input
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">密码</label>
              <Input
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                密码将被加密存储，仅用于 twikit 登录
              </p>
            </div>
          </div>
          
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              取消
            </Button>
            <Button 
              onClick={handleLogin}
              disabled={!username || !email || !password || loginMutation.isPending}
            >
              {loginMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  登录中...
                </>
              ) : (
                <>
                  <Twitter className="w-4 h-4 mr-2" />
                  登录
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
